import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { api, formatApiError, STORE } from "../lib/api";
import { useShop, money, qtyLabel } from "../lib/shop";
import { FulfilmentPicker, FULFILMENT_LABELS } from "../components/FulfilmentPicker";
import { toast } from "sonner";

export default function Checkout() {
    const { basket, user, settings, clearBasket } = useShop();
    const location = useLocation();
    const navigate = useNavigate();
    const [fulfilment, setFulfilment] = useState(location.state?.fulfilment || "collection");
    const [discountCode, setDiscountCode] = useState(location.state?.discount_code || "");
    const [paymentMethod, setPaymentMethod] = useState("online");
    const [pickupDate, setPickupDate] = useState("");
    const [pickupTime, setPickupTime] = useState("");
    const [form, setForm] = useState({ name: "", email: "", phone: "", line1: "", line2: "", city: "Reading", postcode: "", notes: "" });
    const [marketing, setMarketing] = useState(false);
    const [quote, setQuote] = useState(null);
    const [quoteError, setQuoteError] = useState("");
    const [busy, setBusy] = useState(false);
    const paymentTouched = useRef(false);
    const quoteSeq = useRef(0);

    const pickupIso = pickupDate && pickupTime ? new Date(`${pickupDate}T${pickupTime}:00`).toISOString() : null;

    const pickupDays = (() => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date();
            d.setDate(d.getDate() + i);
            days.push(d);
        }
        return days;
    })();

    const slotsForDay = (day) => {
        const [open, close] = STORE_OPENING(day.getDay());
        const now = new Date();
        const isToday = day.toDateString() === now.toDateString();
        const slots = [];
        for (let h = open; h <= close - 1; h++) {
            if (isToday && h <= now.getHours()) continue;
            slots.push(`${String(h).padStart(2, "0")}:00`);
        }
        return slots;
    };
    const STORE_OPENING = (jsDay) => STORE.openingHours[jsDay] || [9, 21];

    useEffect(() => {
        if (user) {
            setForm((f) => ({
                ...f,
                name: f.name || user.name || "",
                email: f.email || user.email || "",
                ...(user.addresses?.[0]
                    ? { line1: user.addresses[0].line1, line2: user.addresses[0].line2 || "", city: user.addresses[0].city, postcode: user.addresses[0].postcode }
                    : {}),
            }));
        }
    }, [user]);

    useEffect(() => {
        if (!basket.length) return;
        const seq = ++quoteSeq.current;
        setQuoteError("");
        api.post("/shop/quote", {
            items: basket.map((i) => ({ product_id: i.product_id, variation: i.variation, qty: i.qty })),
            discount_code: discountCode || undefined,
            fulfilment,
            payment_method: paymentMethod,
            pickup_time: fulfilment === "collection" ? pickupIso || undefined : undefined,
        })
            .then((r) => { if (seq === quoteSeq.current) setQuote(r.data); })
            .catch((err) => {
                if (seq !== quoteSeq.current) return;
                setQuote(null);
                setQuoteError(formatApiError(err.response?.data?.detail));
            });
    }, [basket, fulfilment, discountCode, paymentMethod, pickupIso]);

    // Switching fulfilment: if pay-at-shop was selected and the new method isn't collection, fall back to online
    // in the same event so the quote request never fires with an invalid combination.
    const changeFulfilment = (v) => {
        setFulfilment(v);
        if (v !== "collection" && paymentMethod === "shop") {
            paymentTouched.current = false;
            setPaymentMethod("online");
        }
    };

    // Under the online minimum with collection: pre-select pay at the shop (unless the customer picked a method themselves).
    useEffect(() => {
        if (quote?.online_below_min && fulfilment === "collection" && paymentMethod === "online" && !paymentTouched.current) {
            setPaymentMethod("shop");
        }
    }, [quote, fulfilment, paymentMethod]);

    if (!basket.length)
        return (
            <main className="min-h-[50vh] bg-slate-50 flex items-center justify-center" data-testid="checkout-empty">
                <div className="text-center">
                    <p className="text-slate-500 mb-4">Your basket is empty.</p>
                    <Link to="/products" className="text-[#0033A0] font-bold hover:underline">Back to products</Link>
                </div>
            </main>
        );

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/shop/checkout", {
                items: basket.map((i) => ({ product_id: i.product_id, variation: i.variation, qty: i.qty })),
                discount_code: discountCode || undefined,
                fulfilment,
                payment_method: paymentMethod,
                pickup_time: fulfilment === "collection" ? pickupIso : undefined,
                name: form.name,
                email: form.email,
                phone: form.phone,
                address: fulfilment !== "collection"
                    ? { line1: form.line1, line2: form.line2, city: form.city, postcode: form.postcode }
                    : undefined,
                notes: form.notes,
                marketing_consent: marketing,
                origin_url: window.location.origin,
            });
            clearBasket();
            if (data.pay_at_shop) {
                navigate(`/track/${data.order_number}?token=${data.tracking_token}&placed=shop`);
            } else {
                window.location.href = data.checkout_url;
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Checkout failed — please try again"));
            setBusy(false);
        }
    };

    const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033A0]";

    return (
        <main data-testid="checkout-page" className="bg-slate-50 min-h-screen py-10 md:py-16">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900 mb-8" data-testid="checkout-heading">Checkout</h1>
                <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6">
                        {!user && (
                            <p className="bg-white rounded-2xl border border-slate-200 p-4 text-sm text-slate-600" data-testid="checkout-guest-note">
                                Checking out as a guest.{" "}
                                <Link to="/account" className="text-[#0033A0] font-bold hover:underline" data-testid="checkout-login-link">
                                    Sign in or create an account
                                </Link>{" "}
                                to track orders more easily.
                            </p>
                        )}

                        <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="checkout-details">
                            <h2 className="font-heading font-extrabold text-lg text-slate-900 mb-4">Your Details</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="co-name" className="text-sm font-bold text-slate-900">Full name *</label>
                                    <input id="co-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="checkout-name" />
                                </div>
                                <div>
                                    <label htmlFor="co-email" className="text-sm font-bold text-slate-900">Email *</label>
                                    <input id="co-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="checkout-email" />
                                </div>
                                <div>
                                    <label htmlFor="co-phone" className="text-sm font-bold text-slate-900">Phone</label>
                                    <input id="co-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="checkout-phone" />
                                </div>
                            </div>
                        </section>

                        <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="checkout-fulfilment">
                            <h2 className="font-heading font-extrabold text-lg text-slate-900 mb-4">Delivery or Collection</h2>
                            <div className="mb-4">
                                <FulfilmentPicker value={fulfilment} onChange={changeFulfilment} testIdPrefix="checkout-fulfilment" />
                            </div>
                            {fulfilment !== "collection" && (
                                <div className="grid sm:grid-cols-2 gap-4" data-testid="checkout-address">
                                    <div className="sm:col-span-2">
                                        <label htmlFor="co-line1" className="text-sm font-bold text-slate-900">Address line 1 *</label>
                                        <input id="co-line1" required value={form.line1} onChange={set("line1")} className={inputCls} data-testid="checkout-line1" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="co-line2" className="text-sm font-bold text-slate-900">Address line 2</label>
                                        <input id="co-line2" value={form.line2} onChange={set("line2")} className={inputCls} data-testid="checkout-line2" />
                                    </div>
                                    <div>
                                        <label htmlFor="co-city" className="text-sm font-bold text-slate-900">Town / City *</label>
                                        <input id="co-city" required value={form.city} onChange={set("city")} className={inputCls} data-testid="checkout-city" />
                                    </div>
                                    <div>
                                        <label htmlFor="co-postcode" className="text-sm font-bold text-slate-900">Postcode *</label>
                                        <input id="co-postcode" required value={form.postcode} onChange={set("postcode")} className={inputCls} data-testid="checkout-postcode" />
                                    </div>
                                </div>
                            )}
                            {fulfilment === "collection" && (
                                <div className="space-y-4" data-testid="pickup-picker">
                                    <p className="text-sm text-slate-500" data-testid="checkout-collection-note">
                                        Collect from 33–35 Whitley Street, Reading, RG2 0EG.
                                    </p>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="pickup-date" className="text-sm font-bold text-slate-900">Collection day</label>
                                            <select id="pickup-date" value={pickupDate}
                                                onChange={(e) => { setPickupDate(e.target.value); setPickupTime(""); }}
                                                className={inputCls} data-testid="pickup-date">
                                                <option value="">Choose a day</option>
                                                {pickupDays.map((d) => {
                                                    const iso = d.toISOString().slice(0, 10);
                                                    const label = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
                                                    const [o, c] = STORE_OPENING(d.getDay());
                                                    return (
                                                        <option key={iso} value={iso} data-testid={`pickup-day-${iso}`}>
                                                            {label} (open {o}:00–{c}:00)
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor="pickup-time" className="text-sm font-bold text-slate-900">Collection time</label>
                                            <select id="pickup-time" value={pickupTime} disabled={!pickupDate}
                                                onChange={(e) => setPickupTime(e.target.value)}
                                                className={inputCls} data-testid="pickup-time">
                                                <option value="">{pickupDate ? "Choose a time" : "Pick a day first"}</option>
                                                {pickupDate && slotsForDay(new Date(`${pickupDate}T12:00`)).map((s) => (
                                                    <option key={s} value={s} data-testid={`pickup-slot-${s}`}>{s}</option>
                                                ))}
                                            </select>
                                            {pickupDate && slotsForDay(new Date(`${pickupDate}T12:00`)).length === 0 && (
                                                <p className="text-xs text-amber-700 mt-1">No more slots today — choose a later day.</p>
                                            )}
                                        </div>
                                    </div>
                                    {quote?.perishable_hold && (
                                        <p className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="perishable-notice">
                                            Your basket includes fresh/perishable items — your order will be held for {quote.perishable_hold_hours} hours after your collection time.
                                        </p>
                                    )}
                                </div>
                            )}
                        </section>

                        <section className="bg-white rounded-2xl border border-slate-200 p-6">
                            <label htmlFor="co-notes" className="text-sm font-bold text-slate-900">Order notes (optional)</label>
                            <textarea id="co-notes" rows={3} value={form.notes} onChange={set("notes")}
                                className={`${inputCls} mt-2`} data-testid="checkout-notes"
                                placeholder="Anything we should know about your order?" />
                            <label className="mt-4 flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)}
                                    data-testid="checkout-marketing" className="mt-0.5 accent-[#0033A0]" />
                                Keep me updated with news and offers from Freddie's by email. Unsubscribe anytime.
                            </label>
                        </section>
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 p-6 h-fit" data-testid="checkout-summary">
                        <h2 className="font-heading font-extrabold text-lg text-slate-900 mb-4">Your Order</h2>
                        <ul className="space-y-3 text-sm mb-4">
                            {basket.map((i) => (
                                <li key={`${i.product_id}|${i.variation || ""}`} className="flex justify-between gap-2">
                                    <span className="text-slate-600">{qtyLabel(i)} × {i.name}{i.variation ? ` (${i.variation})` : ""}</span>
                                    <span className="font-bold whitespace-nowrap">{money(i.price * i.qty)}</span>
                                </li>
                            ))}
                        </ul>
                        {quote && (
                            <dl className="space-y-2 text-sm border-t border-slate-100 pt-4" data-testid="checkout-totals">
                                <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd className="font-bold">{money(quote.subtotal)}</dd></div>
                                {quote.discount_amount > 0 && (
                                    <div className="flex justify-between text-green-700"><dt>Discount</dt><dd className="font-bold">-{money(quote.discount_amount)}</dd></div>
                                )}
                                <div className="flex justify-between"><dt className="text-slate-500">{FULFILMENT_LABELS[quote.fulfilment] || "Collection"}</dt><dd className="font-bold">{quote.delivery_fee > 0 ? money(quote.delivery_fee) : "Free"}</dd></div>
                                <div className="flex justify-between text-lg border-t border-slate-100 pt-2">
                                    <dt className="font-heading font-extrabold">Total</dt>
                                    <dd className="font-heading font-extrabold text-[#0033A0]" data-testid="checkout-total">{money(quote.total)}</dd>
                                </div>
                            </dl>
                        )}
                        <div className="mt-5 border-t border-slate-100 pt-5" data-testid="checkout-payment">
                            <h3 className="font-heading font-extrabold text-sm text-slate-900 mb-3">How would you like to pay?</h3>
                            <div className="grid grid-cols-1 gap-2" role="radiogroup" aria-label="Payment method">
                                <button type="button" role="radio" aria-checked={paymentMethod === "online"}
                                    data-testid="payment-online"
                                    onClick={() => { paymentTouched.current = true; setPaymentMethod("online"); }}
                                    className={`rounded-xl px-4 py-3 text-left border-2 transition-colors ${
                                        paymentMethod === "online" ? "border-[#0033A0] bg-[#0033A0] text-white" : "border-slate-200 text-slate-600 hover:border-[#0033A0]"}`}>
                                    <span className="block font-heading font-bold text-sm">Pay online now</span>
                                    <span className={`block text-xs mt-0.5 ${paymentMethod === "online" ? "text-blue-100" : "text-slate-400"}`}>
                                        Card via Stripe · minimum spend {money(settings.online_min_spend ?? 30)}
                                    </span>
                                </button>
                                <button type="button" role="radio" aria-checked={paymentMethod === "shop"}
                                    data-testid="payment-shop"
                                    disabled={fulfilment !== "collection"}
                                    onClick={() => { paymentTouched.current = true; setPaymentMethod("shop"); }}
                                    className={`rounded-xl px-4 py-3 text-left border-2 transition-colors ${
                                        paymentMethod === "shop"
                                            ? "border-[#0033A0] bg-[#0033A0] text-white"
                                            : fulfilment !== "collection"
                                              ? "border-slate-100 text-slate-300 bg-slate-50 cursor-not-allowed"
                                              : "border-slate-200 text-slate-600 hover:border-[#0033A0]"}`}>
                                    <span className="block font-heading font-bold text-sm">Pay at the shop</span>
                                    <span className={`block text-xs mt-0.5 ${paymentMethod === "shop" ? "text-blue-100" : fulfilment !== "collection" ? "text-slate-300" : "text-slate-400"}`}>
                                        {fulfilment === "collection" ? "Cash or card on collection · no minimum" : "Click & Collect orders only"}
                                    </span>
                                </button>
                            </div>
                            {paymentMethod === "online" && quote?.online_below_min && (
                                <p className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="min-spend-warning">
                                    {fulfilment === "collection"
                                        ? `Online payment needs a minimum spend of ${money(settings.online_min_spend ?? 30)} — add ${money((settings.online_min_spend ?? 30) - (quote.subtotal - quote.discount_amount))} more, or choose pay at the shop.`
                                        : `Online payment needs a minimum spend of ${money(settings.online_min_spend ?? 30)} — add ${money((settings.online_min_spend ?? 30) - (quote.subtotal - quote.discount_amount))} more to check out with delivery.`}
                                </p>
                            )}
                        </div>
                        <button type="submit" disabled={busy || !quote || (paymentMethod === "online" && !!quote?.online_below_min)} data-testid="pay-button"
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0033A0] py-3.5 font-heading font-bold text-white hover:bg-[#002277] transition-colors disabled:opacity-50">
                            <Lock className="h-4 w-4" />
                            {busy
                                ? "Placing your order…"
                                : paymentMethod === "shop"
                                  ? "Place Order — Pay at the Shop"
                                  : quote?.online_below_min
                                    ? `Add ${money((settings.online_min_spend ?? 30) - (quote.subtotal - quote.discount_amount))} more to pay online`
                                    : "Pay Securely with Stripe"}
                        </button>
                        {quoteError && (
                            <p className="mt-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" role="alert" data-testid="checkout-quote-error">
                                {quoteError}
                            </p>
                        )}
                        <p className="mt-3 text-xs text-slate-400 text-center">
                            {paymentMethod === "shop"
                                ? "No payment taken now — pay by cash or card when you collect."
                                : "Payments are handled by Stripe. We never see or store your card details."}
                        </p>
                    </div>
                </form>
            </div>
        </main>
    );
}
